import type { PrimeVueLocaleOptions } from 'primevue/config';
import { useTranslation } from 'i18next-vue';
import { defineStore } from 'pinia';
import { usePrimeVue } from 'primevue/config';
import {
  computed,
  nextTick,
  ref,
  toRaw,
} from 'vue';

import useConfig from '@modules/config/composables/useConfig';
import { useI18nQuery } from '@modules/i18n/queries';
import { watchImmediate } from '@vueuse/core';

import { useI18nChangeLocaleMutation } from '../mutations';

async function getPrimeVueLocaleData(locale: string): Promise<PrimeVueLocaleOptions> {
  try {
    return await import(`../../../locales/primevue/${locale}.json`);
  } catch {
    try {
      return await import(`../../../locales/primevue/${locale.split('_')[0]}.json`);
    } catch {
      return await import('@/locales/primevue/en.json');
    }
  }
}

const useI18nStore = defineStore('localization', () => {
  const primevue = usePrimeVue();
  const { config } = useConfig();
  const { i18next } = useTranslation();
  const isInitialized = ref(false);
  const { mutateAsync } = useI18nChangeLocaleMutation();

  const configLocale = computed(() => config.value?.locale || '');
  const currentLocale = computed(() => i18next.language || configLocale.value);
  const fallbackLocale = computed(() => config.value?.fallback_locale);
  const { waitLoaded: loadCurrentLocale } = useI18nQuery(currentLocale);
  const { waitLoaded: loadFallbackLocale } = useI18nQuery(fallbackLocale);

  async function loadResources(): Promise<void> {
    await nextTick();
    const locale = currentLocale.value;

    if (locale) {
      const data = await loadCurrentLocale();
      i18next.addResourceBundle(locale, 'default', toRaw(data));
      isInitialized.value = true;
    }
  }

  async function loadFallbackResources(): Promise<void> {
    await nextTick();
    const locale = fallbackLocale.value;

    if (locale) {
      const data = await loadFallbackLocale();
      i18next.addResourceBundle(locale, 'default', toRaw(data));
    }
  }

  async function changeFrontendLocale(locale: string): Promise<void> {
    await i18next.changeLanguage(locale);
    await loadResources();
  }

  async function changeFrontendFallbackLocale(locale: string): Promise<void> {
    i18next.options.fallbackLng = locale;
    await loadFallbackResources();
  }

  async function setLocale(locale: string): Promise<void> {
    await mutateAsync(locale);
    await changeFrontendLocale(locale);
    i18next.emit('localeChanged');
  }

  function setPrimeVueLocales(localeOptions: PrimeVueLocaleOptions): void {
    primevue.config.locale = {
      ...primevue.config.locale,
      ...localeOptions,
    };
  }

  watchImmediate(configLocale, async (newValue) => {
    if (newValue) {
      const options = await getPrimeVueLocaleData(newValue);
      setPrimeVueLocales(options);
      await changeFrontendLocale(newValue);
    }
  });

  watchImmediate(fallbackLocale, async (newValue) => {
    if (newValue) {
      await changeFrontendFallbackLocale(newValue);
    }
  });

  return {
    isInitialized,
    currentLocale,
    fallbackLocale,
    i18next,
    setLocale,
  };
});

export default useI18nStore;
